import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, Coffee, Soup, Apple, UtensilsCrossed } from "lucide-react";
import Header from "@/components/Header";

const mealIcons: Record<string, any> = {
  Breakfast: Coffee,
  Lunch: Soup,
  Snack: Apple,
  Dinner: UtensilsCrossed,
};

type MacroKeys = "calories" | "protein" | "carbs" | "fat";

interface NutritionData {
  dailyTarget: Record<MacroKeys, number>;
  consumed: Record<MacroKeys, number>;
  meals: {
    time: string;
    foods: { name: string; calories: number; protein: number; carbs: number; fat: number }[];
  }[];
  fitness?: {
    workout_type?: string;
    duration_minutes?: number;
  };
}

const Nutrition = () => {
  const [age, setAge] = useState<number | "">("");
  const [bmi, setBmi] = useState<number | "">("");
  const [gender, setGender] = useState<string>("");
  const [activity, setActivity] = useState<string>("");
  const [goal, setGoal] = useState<string>("");

  const [data, setData] = useState<NutritionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!age || !bmi || !gender || !activity || !goal) {
      setError("Please fill all fields.");
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      const nutritionRes = await fetch("https://tobex.com.ng/hms/python/nutrition/recommend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ age, bmi, gender, activity, goal }),
      });

      if (!nutritionRes.ok) {
        const errData = await nutritionRes.json();
        throw new Error(errData.error || "Failed to fetch nutrition data");
      }
      const nutritionResult: NutritionData = await nutritionRes.json();

      const fitnessRes = await fetch("https://tobex.com.ng/hms/python/fitness/plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ age, bmi, gender, activity, goal }),
      });

      if (!fitnessRes.ok) {
        const errData = await fitnessRes.json();
        throw new Error(errData.error || "Failed to fetch fitness data");
      }
      const fitnessResult = await fitnessRes.json();

      setData({ ...nutritionResult, fitness: fitnessResult });
    } catch (err: any) {
      setError(err.message || "Failed to fetch data. Make sure Flask API is running.");
    } finally {
      setLoading(false);
    }
  };

  const MacroBar = ({
    label,
    current,
    target,
    color,
  }: {
    label: string;
    current: number;
    target: number;
    color: string;
  }) => {
    const percentage = target ? Math.min((current / target) * 100, 100) : 0;
    return (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="font-medium">{label}</span>
          <span className="text-muted-foreground">
            {current} / {target}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full ${color} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Your Personalized Nutrition Strategy
          </h1>
          <p className="text-muted-foreground">
            Fuel your body with precision-guided meal and workout plans.
          </p>
        </div>

        {/* User Inputs */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-muted-foreground">Age</label>
            <input
              type="number"
              placeholder="Age"
              value={age}
              onChange={(e) => setAge(e.target.value === "" ? "" : Number(e.target.value))}
              className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-muted-foreground">BMI</label>
            <input
              type="number"
              placeholder="BMI"
              value={bmi}
              onChange={(e) => setBmi(e.target.value === "" ? "" : Number(e.target.value))}
              className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-muted-foreground">Gender</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="" disabled>Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-muted-foreground">Activity</label>
            <select
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="" disabled>Select Activity</option>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-muted-foreground">Goal</label>
            <select
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="px-3 py-2 rounded-md border border-muted focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="" disabled>Select Goal</option>
              <option value="weight_loss">Lose Weight</option>
              <option value="maintain">Maintain</option>
              <option value="muscle_gain">Gain Muscle</option>
            </select>
          </div>
          <div className="flex flex-col">
            <Button onClick={handleSubmit} className="mt-6">Get Plan</Button>
          </div>
        </div>

        {/* Loading & Error */}
        {loading && <p>Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {/* Nutrition Dashboard */}
        {data && (
          <>
            <div className="mb-8 animate-scale-in">
              <Card className="border shadow-sm">
                <CardHeader>
                  <CardTitle className="text-2xl">Today's Nutritional Dashboard</CardTitle>
                  <CardDescription>
                    Monitor your daily intake against your personalized targets.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-4">
                    {(["calories", "protein", "carbs", "fat"] as MacroKeys[]).map((key) => (
                      <div key={key} className="text-center p-4 rounded-xl bg-muted">
                        <div className="text-3xl font-bold">
                          {data?.consumed?.[key] ?? 0}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          / {data?.dailyTarget?.[key] ?? 0} {key === "calories" ? "kcal" : "g"}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <MacroBar
                      label="Protein"
                      current={data?.consumed?.protein ?? 0}
                      target={data?.dailyTarget?.protein ?? 0}
                      color="bg-orange-500"
                    />
                    <MacroBar
                      label="Carbohydrates"
                      current={data?.consumed?.carbs ?? 0}
                      target={data?.dailyTarget?.carbs ?? 0}
                      color="bg-yellow-500"
                    />
                    <MacroBar
                      label="Fats"
                      current={data?.consumed?.fat ?? 0}
                      target={data?.dailyTarget?.fat ?? 0}
                      color="bg-green-500"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Meal Plan */}
            <div className="space-y-4 animate-slide-up">
              <h2 className="text-2xl font-semibold">Today's Meal Plan</h2>
              {data.meals?.map((meal, index) => {
                const Icon = mealIcons[meal.time] || Coffee;
                return (
                  <Card key={index} className="border shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Icon className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{meal.time}</CardTitle>
                            <p className="text-sm text-muted-foreground">{meal.foods?.length ?? 0} items</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">
                            {meal.foods?.reduce((sum, f) => sum + (f.calories ?? 0), 0)}
                            {" "}kcal
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {meal.foods?.reduce((sum, f) => sum + (f.protein ?? 0), 0)}
                            {" "}g Protein
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {meal.foods?.map((food, foodIndex) => (
                          <div
                            key={foodIndex}
                            className="flex justify-between items-center py-2 border-b last:border-0"
                          >
                            <span className="text-sm font-medium">• {food.name}</span>
                            <div className="flex gap-3 text-xs text-muted-foreground">
                              <span>{food.calories ?? 0} kcal</span>
                              <span className="text-orange-600">{food.protein ?? 0}g P</span>
                              <span className="text-yellow-600">{food.carbs ?? 0}g C</span>
                              <span className="text-green-600">{food.fat ?? 0}g F</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button variant="secondary" size="sm">
                          <PlusCircle className="h-4 w-4 mr-2" />
                          Log Meal
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Fitness Plan */}
            {data.fitness && (
              <div className="mt-8">
                <h2 className="text-2xl font-semibold">Recommended Workout</h2>
                <p>Type: {data.fitness.workout_type ?? "N/A"}</p>
                <p>Duration: {data.fitness.duration_minutes ?? 0} mins</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Nutrition;
